/**
 * Monitoring and metrics system for analytics spam protection.
 *
 * This module provides comprehensive monitoring of the debounce and deduplication
 * systems to track their effectiveness and performance.
 */

import { getDebounceMetrics, getDebounceInfo } from '../utils/debounced-track.js';
import { getDeduplicationStats } from '../utils/event-deduplication.js';
import { getConfiguredEvents, getEventsByFrequency } from '../config/debounce-config.js';

export interface SpamProtectionMetrics {
  /** Total analytics calls made */
  totalCalls: number;
  /** Calls that were debounced */
  debouncedCalls: number;
  /** Events blocked by deduplication */
  duplicateBlocked: number;
  /** Events tracked immediately */
  immediateTracked: number;
  /** Events tracked after debouncing */
  debouncedTracked: number;
  /** Deduplication store statistics */
  deduplication: {
    totalTracked: number;
    activeEvents: number;
    expiredEvents: number;
  };
  /** Spam protection effectiveness */
  effectiveness: {
    /** Percentage of calls that were spam (blocked duplicates) */
    spamRate: number;
    /** Percentage of calls that were debounced */
    debounceRate: number;
    /** Total events prevented from reaching analytics providers */
    totalPrevented: number;
    /** Percentage of events prevented */
    preventionRate: number;
    /** Total events processed */
    totalProcessed: number;
  };
}

export interface DebounceConfigSummary {
  /** Total number of configured events */
  totalConfigured: number;
  /** Events by frequency category */
  byFrequency: {
    high: number;
    medium: number;
    low: number;
    none: number;
  };
  /** Active debounced functions */
  activeFunctions: number;
  /** Functions with pending calls */
  pendingFunctions: number;
}

/**
 * Performance tracking for the spam protection system
 */
class SpamProtectionMonitor {
  private startTime: number = Date.now();
  private reportInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.startPeriodicReporting();
  }

  /**
   * Get comprehensive spam protection metrics
   */
  getMetrics(): SpamProtectionMetrics {
    const debounceMetrics = getDebounceMetrics();
    const deduplicationStats = getDeduplicationStats();

    const totalPrevented = debounceMetrics.duplicateBlocked;
    const totalProcessed = debounceMetrics.immediateTracked + debounceMetrics.debouncedTracked;
    const totalCalls = debounceMetrics.totalCalls;

    return {
      totalCalls: debounceMetrics.totalCalls,
      debouncedCalls: debounceMetrics.debouncedCalls,
      duplicateBlocked: debounceMetrics.duplicateBlocked,
      immediateTracked: debounceMetrics.immediateTracked,
      debouncedTracked: debounceMetrics.debouncedTracked,
      deduplication: deduplicationStats,
      effectiveness: {
        spamRate: totalCalls > 0 ? (debounceMetrics.duplicateBlocked / totalCalls) * 100 : 0,
        debounceRate: totalCalls > 0 ? (debounceMetrics.debouncedCalls / totalCalls) * 100 : 0,
        totalPrevented,
        preventionRate: totalCalls > 0 ? (totalPrevented / totalCalls) * 100 : 0,
        totalProcessed
      }
    };
  }

  /**
   * Get debounce configuration summary
   */
  getConfigSummary(): DebounceConfigSummary {
    const configuredEvents = getConfiguredEvents();
    const debounceInfo = getDebounceInfo();

    return {
      totalConfigured: configuredEvents.length,
      byFrequency: {
        high: getEventsByFrequency('high').length,
        medium: getEventsByFrequency('medium').length,
        low: getEventsByFrequency('low').length,
        none: getEventsByFrequency('none').length
      },
      activeFunctions: debounceInfo.length,
      pendingFunctions: debounceInfo.filter((info) => info.hasPendingCalls).length
    };
  }

  /**
   * Get detailed information about active debounced functions
   */
  getActiveDebounceInfo() {
    return getDebounceInfo();
  }

  /**
   * Generate a comprehensive report
   */
  generateReport(): {
    timestamp: number;
    uptime: number;
    metrics: SpamProtectionMetrics;
    config: DebounceConfigSummary;
    activeDebounce: ReturnType<typeof getDebounceInfo>;
  } {
    const now = Date.now();
    return {
      timestamp: now,
      uptime: now - this.startTime,
      metrics: this.getMetrics(),
      config: this.getConfigSummary(),
      activeDebounce: this.getActiveDebounceInfo()
    };
  }

  /**
   * Log metrics to console (development only)
   */
  logMetrics(): void {
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    const report = this.generateReport();
    const { metrics, config } = report;

    console.group('📊 Analytics Spam Protection Report');
    console.log('⏱️ Uptime:', Math.round(report.uptime / 1000), 'seconds');
    console.log('📈 Total Calls:', metrics.totalCalls);
    console.log(
      '🚫 Spam Blocked:',
      metrics.duplicateBlocked,
      `(${metrics.effectiveness.spamRate.toFixed(1)}%)`
    );
    console.log(
      '⏳ Debounced:',
      metrics.debouncedCalls,
      `(${metrics.effectiveness.debounceRate.toFixed(1)}%)`
    );
    console.log('✅ Tracked:', metrics.immediateTracked + metrics.debouncedTracked);
    console.log('🛡️ Prevention Rate:', `${metrics.effectiveness.preventionRate.toFixed(1)}%`);
    console.log('⚙️ Configured Events:', config.totalConfigured);
    console.log('🔄 Active Functions:', config.activeFunctions);
    console.log('⏸️ Pending Functions:', config.pendingFunctions);
    console.groupEnd();
  }

  /**
   * Start periodic reporting (development only)
   */
  private startPeriodicReporting(): void {
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    // Report every 30 seconds in development
    this.reportInterval = setInterval(() => {
      const metrics = this.getMetrics();

      // Only log if there's been activity
      if (metrics.totalCalls > 0) {
        this.logMetrics();
      }
    }, 30000);
  }

  /**
   * Stop periodic reporting
   */
  stopReporting(): void {
    if (this.reportInterval) {
      clearInterval(this.reportInterval);
      this.reportInterval = null;
    }
  }

  /**
   * Check if spam protection is working effectively
   */
  isEffective(): {
    effective: boolean;
    issues: string[];
    recommendations: string[];
  } {
    const metrics = this.getMetrics();
    const config = this.getConfigSummary();
    const issues: string[] = [];
    const recommendations: string[] = [];

    // Check for high spam rate
    if (metrics.effectiveness.spamRate > 20) {
      issues.push(`High spam rate: ${metrics.effectiveness.spamRate.toFixed(1)}%`);
      recommendations.push('Consider reducing debounce delays for high-frequency events');
    }

    // Check for low debounce usage
    if (metrics.totalCalls > 100 && metrics.effectiveness.debounceRate < 10) {
      issues.push(`Low debounce usage: ${metrics.effectiveness.debounceRate.toFixed(1)}%`);
      recommendations.push('Review event configurations to ensure appropriate debouncing');
    }

    // Check for memory usage (too many active events in deduplication)
    if (metrics.deduplication.activeEvents > 1000) {
      issues.push(`High memory usage: ${metrics.deduplication.activeEvents} active events`);
      recommendations.push('Consider reducing deduplication windows for some events');
    }

    // Check for pending functions that might indicate issues
    if (config.pendingFunctions > config.activeFunctions * 0.5) {
      issues.push(`Many pending functions: ${config.pendingFunctions}/${config.activeFunctions}`);
      recommendations.push('Check for potential performance issues or excessive event firing');
    }

    return {
      effective: issues.length === 0,
      issues,
      recommendations
    };
  }

  /**
   * Reset all metrics and restart monitoring
   */
  reset(): void {
    this.startTime = Date.now();
  }

  /**
   * Cleanup monitoring resources
   */
  destroy(): void {
    this.stopReporting();
  }
}

/**
 * Global spam protection monitor instance
 */
const monitor = new SpamProtectionMonitor();

/**
 * Get spam protection metrics
 */
export function getSpamProtectionMetrics(): SpamProtectionMetrics {
  return monitor.getMetrics();
}

/**
 * Get configuration summary
 */
export function getSpamProtectionConfigSummary(): DebounceConfigSummary {
  return monitor.getConfigSummary();
}

/**
 * Generate a comprehensive spam protection report
 */
export function generateSpamProtectionReport() {
  return monitor.generateReport();
}

/**
 * Check if spam protection is working effectively
 */
export function checkSpamProtectionEffectiveness() {
  return monitor.isEffective();
}

/**
 * Log current metrics to console (development only)
 */
export function logSpamProtectionMetrics(): void {
  monitor.logMetrics();
}

/**
 * Reset spam protection monitoring
 */
export function resetSpamProtectionMonitoring(): void {
  monitor.reset();
}

/**
 * Expose monitor for advanced usage
 */
export { monitor as spamProtectionMonitor };
